import React from 'react';

import { Badges } from '@pkg/components/Sections/Groups/GroupHeader';
import { StyledYatHand } from '@pkg/components/Sections/Groups/GroupHeader/GroupHeaderImage/styles';
import { StyledFollowerInfo } from '@pkg/components/Sections/Groups/GroupHeaderHome/styles';

import { getMashedUrl } from '@app/helpers';

import { GroupItemAvatarBadge } from '../../../GroupItemAvatarBadge';
import {
    GroupItemAvatar,
    GroupItemAvatarBackground,
    GroupItemAvatarHolder,
    GroupItemAvatarImage,
    GroupItemAvatarPlaceholder,
    GroupItemBadges,
    GroupItemInfoText,
    GroupItemName,
} from '../../../styles';
import { type GroupItemProps } from '../../../types';
import { GroupItemInfo, GroupItemWrapper } from '../../styles';

export enum GROUP_ASSOCIATION {
    follower = 'follower',
    member = 'member',
    owner = 'owner',
}

const ANIMATION_DURATION = 0.2;
const VARIANT_BADGES = {
    inactive: {
        opacity: 0,
        y: 8,
        transition: {
            duration: ANIMATION_DURATION,
        },
    },
    active: {
        opacity: 1,
        y: 0,
        transition: {
            duration: ANIMATION_DURATION,
            delay: 0.06,
        },
    },
};

const VARIANT_INFO = {
    inactive: {
        paddingBottom: 0,
        transition: {
            bounce: 0,
            duration: ANIMATION_DURATION,
        },
    },
    active: {
        paddingBottom: 22,
        transition: {
            bounce: 0,
            duration: ANIMATION_DURATION,
        },
    },
};

export const GroupItem = ({
    name,
    avatarUrl,
    isOwner,
    isMember,
    privacy,
    members,
    followers,
    membershipClickable,
    id,
    onClick,
}: GroupItemProps) => {
    const isPrivate = privacy === 'Private';

    let groupAssociation: GROUP_ASSOCIATION = GROUP_ASSOCIATION.follower;
    if (isOwner) {
        groupAssociation = GROUP_ASSOCIATION.owner;
    } else if (isMember) {
        groupAssociation = GROUP_ASSOCIATION.member;
    }

    const mashedImageUrl =
        avatarUrl &&
        getMashedUrl({
            width: 500,
            url: avatarUrl,
        });

    return (
        <GroupItemWrapper initial="inactive" whileHover="active" onClick={onClick}>
            <GroupItemAvatar isPrivate={isPrivate}>
                <GroupItemAvatarHolder isPrivate={isPrivate}>
                    {mashedImageUrl ? (
                        <GroupItemAvatarImage avatarUrl={mashedImageUrl} />
                    ) : (
                        <GroupItemAvatarPlaceholder>
                            <StyledYatHand />
                        </GroupItemAvatarPlaceholder>
                    )}
                </GroupItemAvatarHolder>
                {isPrivate && <GroupItemAvatarBackground />}
                {(isOwner || isMember) && <GroupItemAvatarBadge type={isOwner ? 'owner' : 'member'} />}
            </GroupItemAvatar>
            <GroupItemInfo>
                <GroupItemInfoText variants={VARIANT_INFO}>
                    <GroupItemName title={name}>{name}</GroupItemName>
                    <StyledFollowerInfo
                        membershipClickable={membershipClickable}
                        podId={id}
                        membersCount={members}
                        followerCount={followers}
                    />
                </GroupItemInfoText>
                <GroupItemBadges variants={VARIANT_BADGES}>
                    <Badges
                        isFollowing={groupAssociation === 'follower'}
                        hasMembership={groupAssociation === 'member'}
                        isOwner={groupAssociation === 'owner'}
                        privacy={privacy}
                    />
                </GroupItemBadges>
            </GroupItemInfo>
        </GroupItemWrapper>
    );
};
